import { useState } from 'react';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { getMandatoryFormFields } from '@go-shared/lib/brand';
import { BrandFormFieldName } from '@go-shared/types/brand';
import { GroupForm } from '@go-client/components/GroupBookingForm/Workflows';

export type GroupDetailsFieldName = BrandFormFieldName | 'name';

export function isFieldValueEmpty(groupForm: GroupForm, fieldName: GroupDetailsFieldName): boolean {
  const value = groupForm[fieldName];
  if (typeof value === 'string') return value.trim().length === 0;
  return value === undefined || value === null;
}

// Shared by any page that renders EnterGroupPageFields, so asterisks/errors/submit-gating stay
// consistent wherever group details are entered (EnterGroupPage, SelectGroupPage, ...).
export function useGroupDetailsValidation(groupForm: GroupForm) {
  const { brand } = useServerInfo();
  const [touchedFields, setTouchedFields] = useState<Set<GroupDetailsFieldName>>(new Set());
  // Fields whose input the browser flags as invalid (e.g. an impossible date like 02/31). Their
  // value never reaches the form, so they must block submission explicitly.
  const [invalidFields, setInvalidFields] = useState<Set<GroupDetailsFieldName>>(new Set());

  const mandatoryFields = new Set<GroupDetailsFieldName>([
    'name',
    ...getMandatoryFormFields(brand, 'group_party'),
  ]);

  const formValid =
    invalidFields.size === 0 &&
    [...mandatoryFields].every(fieldName => !isFieldValueEmpty(groupForm, fieldName));

  function handleFieldBlur(fieldName: GroupDetailsFieldName) {
    setTouchedFields(prev => (prev.has(fieldName) ? prev : new Set(prev).add(fieldName)));
  }

  function handleFieldValidityChange(fieldName: GroupDetailsFieldName, valid: boolean) {
    setInvalidFields(prev => {
      if (prev.has(fieldName) !== valid) return prev;
      const next = new Set(prev);
      if (valid) next.delete(fieldName);
      else next.add(fieldName);
      return next;
    });
  }

  return {
    mandatoryFields,
    touchedFields,
    invalidFields,
    formValid,
    handleFieldBlur,
    handleFieldValidityChange,
  };
}
